<template>
  <div class="row bg-white h-100" style="min-height: 100vh;">
    <div class="col-12" style="flex: 1; min-height: 0; display: flex; flex-direction: column;">
      <div class="row gy-2" style="flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 20px;">
        <div class="row gx-4 align-items-center">
            <div class="col-auto my-auto">
              <h5 class="mb-0 header-title">Pricing Services</h5>
              <p class="mb-0 text-xs text-muted">Please use the below filters to navigate the analytics</p>
            </div>
          </div>
        <div v-if="inProgress" class="col-12 mt-4 loading-state">
          <div class="custom-loader">
            <div class="loader-circle"/>
            <div class="loader-circle"/>
            <div class="loader-circle"/>
            <div class="loader-circle"/>
          </div>
          <p class="loading-text">Loading...</p>
        </div>
        <template v-else>
        <div class="col-12">
          <!-- Action row for Supply Chains (Top Tabs) -->
          <div class="dash-actions mb-0">
            <button
                class="dash-action-btn"
                :class="{ 'dash-action-btn--active': activeSupplyChain === 'Commodity' }"
                @click="activeSupplyChain = 'Commodity'"
            >
              Commodity
            </button>
            <button
                v-for="chain in supplyChains"
                :key="chain.code"
                class="dash-action-btn"
                :class="{ 'dash-action-btn--active': activeSupplyChain === chain.code }"
                @click="activeSupplyChain = chain.code"
            >
              {{ chain.name }}
            </button>
          </div>

          <!-- Filter bar -->
          <div class="dash-filterbar mb-2">
            
            <!-- Row 1: Filters -->
            <div class="d-flex align-items-center gap-3">
              <NSelect
                  v-model:value="commodityFilter"
                  :options="commodityOptions"
                  :theme-overrides="inputTheme"
                  placeholder="Select Commodity"
                  size="small"
                  clearable
                  style="width: 200px"
              />
              <div class="dash-filter">
                <NSelect
                    v-model:value="regionFilter"
                    :options="regionOptions"
                    :theme-overrides="inputTheme"
                    placeholder="Select Region"
                    size="small"
                    clearable
                    style="width: 200px"
                />
              </div>

              <!-- Only show the frequency toggle (Weekly/Monthly) when in Data Table view -->
              <div class="dash-filter" v-if="activeView === 'DataTable'">
                <NSelect
                    v-model:value="dataTableFrequency"
                    :options="frequencyOptions"
                    :theme-overrides="inputTheme"
                    size="small"
                    style="width: 150px"
                />
              </div>
            </div>

            <!-- Row 2: Sub tabs -->
            <div class="dash-actions" style="padding: 0; margin: 0;">
              <button 
                class="dash-action-btn"
                :class="{ 'dash-action-btn--active': activeView === 'Dashboard' }"
                @click="activeView = 'Dashboard'"
              >
                Dashboard
              </button>
              <button 
                class="dash-action-btn"
                :class="{ 'dash-action-btn--active': activeView === 'DataTable' }"
                @click="activeView = 'DataTable'"
              >
                Data Table
              </button>
              <button
                v-if="activeView === 'DataTable'"
                class="dash-action-btn"
                @click="handleExportDataTable"
                :disabled="!dataTableRecords || dataTableRecords.length === 0"
                title="Export Data"
              >
                <img :src="DDICONS.download" alt="" class="dash-action-btn__icon" />
                Export Data
              </button>
              <NTooltip trigger="hover" placement="right" :show-arrow="false" style="background-color: #fff3cd; color: #856404; font-size: 11px; padding: 4px 8px; border: 1px solid #ffeeba;">
                <template #trigger>
                  <span style="display: inline-block; cursor: not-allowed;">
                    <button 
                      class="dash-action-btn"
                      :class="{ 'dash-action-btn--active': activeView === 'Forecast' }"
                      disabled
                      style="pointer-events: none;"
                    >
                      Forecast
                    </button>
                  </span>
                </template>
                Coming soon
              </NTooltip>
            </div>
          </div>
        </div>

        <!-- Two column layout for Tables and Sidebar -->
        <div class="col-12 d-flex gap-3 pricing-main-layout" style="flex: 1; min-height: 0;">
          
          <!-- Left Column: Tables -->
          <div style="flex: 1; min-width: 0; display: flex; flex-direction: column;">
            
              <!-- DASHBOARD VIEW: Shows both Weekly and Monthly summary tables -->
            <template v-if="activeView === 'Dashboard'">
              <!-- Weekly Table -->
              <div class="d-flex align-items-center mb-2 mt-2">
                <span class="table-title me-2 text-uppercase" style="cursor: default;">WEEKLY</span>
              </div>
              <div class="table-scroll modern-scrollbar mb-4">
                <NDataTable
                  :columns="weeklyColumns"
                  :data="filteredWeeklyData"
                  :bordered="false"
                  :scroll-x="weeklyTableScrollX"
                  :pagination="weeklyPagination"
                />
              </div>

              <!-- Monthly Table -->
              <div class="d-flex align-items-center mb-2 mt-2">
                <span class="table-title me-2 text-uppercase" style="cursor: default;">MONTHLY</span>
              </div>
              <div class="table-scroll modern-scrollbar">
                <NDataTable
                  :columns="monthlyColumns"
                  :data="monthlyData"
                  :bordered="false"
                  :scroll-x="monthlyTableScrollX"
                  :pagination="monthlyPagination"
                />
              </div>
            </template>

            <!-- DATA TABLE VIEW: Shows a single table with all historical periods -->
            <template v-else-if="activeView === 'DataTable'">
              <div class="d-flex align-items-center mb-2 mt-2">
                <span class="table-title me-2 text-uppercase" style="cursor: default;">{{ dataTableFrequency }}</span>
              </div>
              <div class="table-scroll modern-scrollbar">
                <NDataTable
                  :columns="dataTableColumns"
                  :data="dataTableRecords"
                  :bordered="false"
                  :scroll-x="dataTableScrollX"
                  :pagination="dataTablePagination"
                />
              </div>
            </template>
            
          </div>

          <!-- Right Column: Reports Sidebar -->
          <div class="pricing-reports-sidebar">
            <!-- Weekly Report Card -->
            <div class="report-card mb-4">
              <div class="report-split-group mb-2">
                <button
                  type="button"
                  class="report-main-btn"
                  @click="handleReportClick('Latest Weekly Report')"
                  :disabled="!hasWeeklyReport"
                  :style="!hasWeeklyReport ? disabledReportStyle : activeReportStyle"
                  title="Download Latest Weekly Report"
                >
                  Latest Weekly Report
                </button>
                <NDropdown
                  trigger="click"
                  :options="weeklyHistoricalOptions"
                  :render-option="renderHistoricalDropdownOption"
                  :render-label="renderHistoricalLabel"
                  @select="handleHistoricalSelect"
                  :disabled="!hasWeeklyReport"
                  scrollable
                  style="max-height: 320px; max-width: 380px;"
                >
                  <div style="display: flex; height: 100%;">
                    <NTooltip trigger="hover" placement="top">
                      <template #trigger>
                        <button
                          type="button"
                          class="report-dropdown-btn"
                          :disabled="!hasWeeklyReport"
                          :style="!hasWeeklyReport ? disabledChevronStyle : activeChevronStyle"
                          title="Historical Files"
                        >
                          <i class="fa fa-chevron-down" style="font-size: 10px;"></i>
                        </button>
                      </template>
                      Historical Files
                    </NTooltip>
                  </div>
                </NDropdown>
              </div>
              <img :src="weeklyThumbnailUrl" alt="Weekly Report" class="report-thumb img-fluid" />
            </div>

            <!-- Monthly Report Card -->
            <div class="report-card mb-4">
              <div class="report-split-group mb-2">
                <button
                  type="button"
                  class="report-main-btn"
                  @click="handleReportClick('Latest Monthly Report')"
                  :disabled="!hasMonthlyReport"
                  :style="!hasMonthlyReport ? disabledReportStyle : activeReportStyle"
                  title="Download Latest Monthly Report"
                >
                  Latest Monthly Report
                </button>
                <NDropdown
                  trigger="click"
                  :options="monthlyHistoricalOptions"
                  :render-option="renderHistoricalDropdownOption"
                  :render-label="renderHistoricalLabel"
                  @select="handleHistoricalSelect"
                  :disabled="!hasMonthlyReport"
                  scrollable
                  style="max-height: 320px; max-width: 380px;"
                >
                  <div style="display: flex; height: 100%;">
                    <NTooltip trigger="hover" placement="top">
                      <template #trigger>
                        <button
                          type="button"
                          class="report-dropdown-btn"
                          :disabled="!hasMonthlyReport"
                          :style="!hasMonthlyReport ? disabledChevronStyle : activeChevronStyle"
                          title="Historical Files"
                        >
                          <i class="fa fa-chevron-down" style="font-size: 10px;"></i>
                        </button>
                      </template>
                      Historical Files
                    </NTooltip>
                  </div>
                </NDropdown>
              </div>
              <img :src="monthlyThumbnailUrl" alt="Monthly Report" class="report-thumb img-fluid" />
            </div>

           </div>
        </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, h, computed, watch } from 'vue';
import { NSelect, NDataTable, NTooltip, NPopover, NButton, NDropdown, useMessage } from 'naive-ui';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { DDICONS } from "@/composables/generics";
import { logUserAction, USER_ACTIONS, userEmail } from "@/composables/userLogs";
import defaultPricingCover from '@/assets/pricingDownloadCover.png';
import TrendSparkline from './TrendSparkline.vue';
import { 
  // myPricingServices,
  // supplyChains,
  activeSupplyChain,
  filteredWeeklyData,
  monthlyData,
  fetchPricingServices,
  commodityFilter,
  regionFilter,
  commodityOptions,
  regionOptions,
  SUPPLY_CHAIN_OPTS,
  activeView,
  dataTableFrequency,
  frequencyOptions,
  dataTableRecords,
  fetchedSupplyChains,
  getHistoryDates,
  inProgress
} from '@/composables/pricingServices';
import { SUBSCRIPTION_INFO } from "@/composables/subscription";
import { 
  USER_FILES, 
  getUserFiles, 
  PRICING_SERVICES_FILES, 
  downloadFile as downloadFileComposable 
} from '@/composables/files/list';

const message = useMessage();

// Active & disabled styles for the split button
const activeReportStyle = {
  background: 'linear-gradient(90deg, #213ce5, #2380f2)',
  color: '#ffffff',
  border: 'none',
  borderTopLeftRadius: '4px',
  borderBottomLeftRadius: '4px',
  borderTopRightRadius: '0px',
  borderBottomRightRadius: '0px',
  fontWeight: '600',
  fontSize: '11.5px'
};

const disabledReportStyle = {
  background: '#9ca3af',
  color: '#ffffff',
  border: 'none',
  borderTopLeftRadius: '4px',
  borderBottomLeftRadius: '4px',
  borderTopRightRadius: '0px',
  borderBottomRightRadius: '0px',
  fontSize: '11.5px'
};

const activeChevronStyle = {
  background: '#1d5ec9',
  color: '#ffffff',
  border: 'none',
  borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
  borderTopLeftRadius: '0px',
  borderBottomLeftRadius: '0px',
  borderTopRightRadius: '4px',
  borderBottomRightRadius: '4px',
  padding: '0 8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const disabledChevronStyle = {
  background: '#8a94a6',
  color: '#ffffff',
  border: 'none',
  borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
  borderTopLeftRadius: '0px',
  borderBottomLeftRadius: '0px',
  borderTopRightRadius: '4px',
  borderBottomRightRadius: '4px',
  padding: '0 8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

// Calculate client's subscription start and end bounds for the selected element
const clientSubscriptionRange = computed(() => {
  const selectedElement = activeSupplyChain.value === 'Commodity' 
    ? (commodityFilter.value || commodityOptions.value?.[0]?.value) 
    : activeSupplyChain.value;

  let minStart = null;
  let maxEnd = null;

  const packages = SUBSCRIPTION_INFO.parentSubscriptionPackages || [];
  const matchingPackages = packages.filter(pkg => {
    if (!selectedElement) return true;
    if (pkg.elements && Array.isArray(pkg.elements) && pkg.elements.some(e => e.toLowerCase() === selectedElement.toLowerCase())) {
      return true;
    }
    if (pkg.packageName && pkg.packageName.toLowerCase().includes(selectedElement.toLowerCase())) {
      return true;
    }
    return false;
  });

  const packagesToCheck = matchingPackages.length > 0 ? matchingPackages : packages;

  packagesToCheck.forEach(pkg => {
    let start = null;
    let end = null;
    if (Array.isArray(pkg.effectiveDate) && pkg.effectiveDate.length >= 2) {
      if (pkg.effectiveDate[0]) start = new Date(pkg.effectiveDate[0]);
      if (pkg.effectiveDate[1]) end = new Date(pkg.effectiveDate[1]);
    } else {
      if (pkg.startDate) start = new Date(pkg.startDate);
      if (pkg.endDate) end = new Date(pkg.endDate);
    }

    if (start && !isNaN(start.getTime())) {
      if (!minStart || start < minStart) minStart = start;
    }
    if (end && !isNaN(end.getTime())) {
      if (!maxEnd || end > maxEnd) maxEnd = end;
    }
  });

  return { startDate: minStart, endDate: maxEnd };
});

// Guard ensuring historical files strictly fall within client subscription bounds
const isFileWithinSubscription = (file) => {
  const { startDate: subStart, endDate: subEnd } = clientSubscriptionRange.value;
  const rawDate = file.startDate || file.publishDateStart || file.date;
  if (!rawDate) return true;

  const fileDate = new Date(rawDate);
  if (isNaN(fileDate.getTime())) return true;

  if (subStart && fileDate < subStart) return false;
  if (subEnd && fileDate > subEnd) return false;

  return true;
};

// All available reports for the currently selected commodity/supply chain within subscription
const allSubscribedReports = computed(() => {
  const selectedElement = activeSupplyChain.value === 'Commodity' 
    ? (commodityFilter.value || commodityOptions.value?.[0]?.value) 
    : activeSupplyChain.value;

  const allFiles = [];
  (PRICING_SERVICES_FILES.value || []).forEach(sub => {
    if (selectedElement && sub.element !== selectedElement) return;

    if (sub.folders) {
      sub.folders.forEach(folder => {
        if (folder.files) {
          folder.files.forEach(f => {
            if (!f.isHidden) {
              allFiles.push({
                ...f,
                folderName: folder.folderName || sub.subscriptionName,
                element: sub.element
              });
            }
          });
        }
      });
    }
  });

  return allFiles.filter(isFileWithinSubscription);
});

const isMonthlyFile = (file) => {
  const name = (file.fileName || '').toLowerCase();
  const folder = (file.folderName || '').toLowerCase();
  const display = (file.displayName || '').toLowerCase();
  return name.includes('monthly') || folder.includes('monthly') || display.includes('monthly');
};

const isWeeklyFile = (file) => {
  const name = (file.fileName || '').toLowerCase();
  const folder = (file.folderName || '').toLowerCase();
  const display = (file.displayName || '').toLowerCase();
  return name.includes('weekly') || folder.includes('weekly') || display.includes('weekly');
};

// Historical Monthly Reports Options for Dropdown (newest first, with header at the top)
const monthlyHistoricalFiles = computed(() => {
  const monthlyFiles = allSubscribedReports.value.filter(isMonthlyFile);

  monthlyFiles.sort((a, b) => {
    const dateA = new Date(a.startDate || a.publishDateStart || 0);
    const dateB = new Date(b.startDate || b.publishDateStart || 0);
    return dateB - dateA;
  });

  return monthlyFiles;
});

const monthlyHistoricalOptions = computed(() => {
  const files = monthlyHistoricalFiles.value;
  if (!files.length) return [];

  const headerOption = {
    type: 'render',
    key: 'header-monthly',
    render: () => h(
      'div',
      {
        class: 'historical-dropdown-header',
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px 7px 12px',
          borderBottom: '1px solid #e2e8f0',
          marginBottom: '4px',
          color: '#1e293b',
          fontSize: '12px',
          fontWeight: '700',
          letterSpacing: '0.2px',
          userSelect: 'none',
          cursor: 'default',
          backgroundColor: '#f8fafc'
        }
      },
      [
        h('i', {
          class: 'fa fa-history',
          style: {
            color: '#213ce5',
            fontSize: '13px'
          }
        }),
        h('span', 'Historical Files')
      ]
    )
  };

  const fileOptions = files.map((file, index) => {
    const rawDate = file.startDate || file.publishDateStart;
    let dateLabel = '';
    if (rawDate) {
      const d = new Date(rawDate);
      if (!isNaN(d.getTime())) {
        dateLabel = d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
      }
    }
    const name = file.displayName || file.fileName || 'Monthly Report';
    const tag = index === 0 ? ' (Latest)' : '';
    const fullTitle = dateLabel ? `${dateLabel} - ${name}${tag}` : `${name}${tag}`;
    return {
      label: fullTitle,
      key: String(file.fileUuid || file.id || index),
      file: file,
      tooltip: fullTitle
    };
  });

  return [headerOption, ...fileOptions];
});

// Historical Weekly Reports Options for Dropdown (newest first, with header at the top)
const weeklyHistoricalFiles = computed(() => {
  const weeklyFiles = allSubscribedReports.value.filter(isWeeklyFile);

  weeklyFiles.sort((a, b) => {
    const dateA = new Date(a.startDate || a.publishDateStart || 0);
    const dateB = new Date(b.startDate || b.publishDateStart || 0);
    return dateB - dateA;
  });

  return weeklyFiles;
});

const weeklyHistoricalOptions = computed(() => {
  const files = weeklyHistoricalFiles.value;
  if (!files.length) return [];

  const headerOption = {
    type: 'render',
    key: 'header-weekly',
    render: () => h(
      'div',
      {
        class: 'historical-dropdown-header',
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px 7px 12px',
          borderBottom: '1px solid #e2e8f0',
          marginBottom: '4px',
          color: '#1e293b',
          fontSize: '12px',
          fontWeight: '700',
          letterSpacing: '0.2px',
          userSelect: 'none',
          cursor: 'default',
          backgroundColor: '#f8fafc'
        }
      },
      [
        h('i', {
          class: 'fa fa-history',
          style: {
            color: '#213ce5',
            fontSize: '13px'
          }
        }),
        h('span', 'Historical Files')
      ]
    )
  };

  const fileOptions = files.map((file, index) => {
    const rawDate = file.startDate || file.publishDateStart;
    let dateLabel = '';
    if (rawDate) {
      const d = new Date(rawDate);
      if (!isNaN(d.getTime())) {
        dateLabel = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    }
    const name = file.displayName || file.fileName || 'Weekly Report';
    const tag = index === 0 ? ' (Latest)' : '';
    const fullTitle = dateLabel ? `${dateLabel} - ${name}${tag}` : `${name}${tag}`;
    return {
      label: fullTitle,
      key: String(file.fileUuid || file.id || index),
      file: file,
      tooltip: fullTitle
    };
  });

  return [headerOption, ...fileOptions];
});

// Render function to wrap each historical option in an NTooltip (displays on hover)
const renderHistoricalDropdownOption = ({ node, option }) => {
  if (option.type === 'render' || option.type === 'divider') {
    return node;
  }
  return h(
    NTooltip,
    {
      placement: 'left',
      delay: 150,
      keepAliveOnHover: false
    },
    {
      trigger: () => node,
      default: () => option.tooltip || option.label
    }
  );
};

// Render function for option label with ellipsis so long file names do not break layout
const renderHistoricalLabel = (option) => {
  if (option.type === 'render' || option.type === 'divider') {
    return null;
  }
  return h(
    'div',
    {
      style: {
        maxWidth: '280px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '12px',
        lineHeight: 'inherit',
        height: '100%',
        display: 'flex',
        alignItems: 'center'
      },
      title: option.tooltip || option.label
    },
    h(
      'span',
      {
        style: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'inline-block',
          width: '100%'
        }
      },
      option.label
    )
  );
};

const hasMonthlyReport = computed(() => monthlyHistoricalFiles.value.length > 0);
const hasWeeklyReport = computed(() => weeklyHistoricalFiles.value.length > 0);

const monthlyThumbnailUrl = computed(() => {
  const selectedElement = activeSupplyChain.value === 'Commodity' ? (commodityFilter.value || commodityOptions.value?.[0]?.value) : activeSupplyChain.value;
  const sub = PRICING_SERVICES_FILES.value?.find(s => !selectedElement || s.element === selectedElement);
  return (hasMonthlyReport.value && sub?.thumbnail) ? sub.thumbnail : defaultPricingCover;
});

const weeklyThumbnailUrl = computed(() => {
  const selectedElement = activeSupplyChain.value === 'Commodity' ? (commodityFilter.value || commodityOptions.value?.[0]?.value) : activeSupplyChain.value;
  const sub = PRICING_SERVICES_FILES.value?.find(s => !selectedElement || s.element === selectedElement);
  return (hasWeeklyReport.value && sub?.thumbnail) ? sub.thumbnail : defaultPricingCover;
});

const supplyChains = computed(() => {
  return SUBSCRIPTION_INFO.pricingServicesElements?.filter(item => item.isSupplyChain) || [];
});

const formatSupplyChain = (val) => {
  const opt = SUPPLY_CHAIN_OPTS.find(o => o.value === val);
  return opt ? opt.label : val;
};

const inputTheme = {
  peers: {
    InternalSelection: {
      borderHover: '1px solid #213ce5',
      borderFocus: '1px solid #213ce5',
      borderActive: '1px solid #213ce5',
      boxShadowFocus: '0 0 0 2px rgba(33, 60, 229, 0.2)',
      boxShadowActive: '0 0 0 2px rgba(33, 60, 229, 0.2)',
      boxShadowHover: 'none',
      caretColor: '#213ce5',
      arrowColorActive: '#213ce5',
      arrowColorHover: '#213ce5',
      fontFamily: "'GilroyMedium', sans-serif",
      fontSize: '11px !important',
    },
    InternalSelectMenu: {
      optionColorActive: 'rgba(33, 60, 229, 0.08)',
      optionColorActivePending: 'rgba(33, 60, 229, 0.12)',
      optionColorPending: 'rgba(33, 60, 229, 0.05)',
      optionTextColorActive: '#213ce5',
      optionTextColorChildActive: '#213ce5',
      optionCheckColor: '#213ce5',
      actionDividerColor: '#213ce5',
      loadingColor: '#213ce5',
      scrollbarColor: 'rgba(33, 60, 229, 0.3)',
      scrollbarColorHover: 'rgba(33, 60, 229, 0.5)',
      fontFamily: "'GilroyMedium', sans-serif",
      fontSize: '11px !important',
    }
  }
};

// Renders a sparkline (mini line chart) with an interactive popover for the price trend
const renderTrend = (row) => {
  // Use the new Vue component to handle the sparkline rendering and the click popover
  return h(TrendSparkline, { row: row });
};

const renderRational = (row) => {
  const icon = h('i', { 
    class: row.rationalComment ? 'fas fa-lightbulb' : 'fas fa-lightbulb text-muted',
    style: row.rationalComment ? 'cursor: pointer; color: #ffc107; font-size: 16px;' : ''
  });
  
  if (!row.rationalComment) {
    return icon;
  }
  
  return h(
    NPopover,
    { trigger: 'click', placement: 'left', style: 'max-width: 300px;' },
    {
      trigger: () => icon,
      default: () => row.rationalComment
    }
  );
};

// Render function for W-o-W, M-o-M, and Y-o-Y
const renderChange = (value, type) => {
  if (value === undefined || value === null) return h('span', { class: 'text-muted' }, '-');
  
  // Set background color based on column type using inline styles to ensure it applies
  const bgColor = type === 'yoy' ? '#d1e7dd' : '#fff3cd';
  
  return h(
    'span', 
    { 
      style: `background-color: ${bgColor} !important; border-radius: 4px; display: inline-block; min-width: 50px; padding: 4px 8px;`
    }, 
    `${value > 0 ? '+' : ''}${value}%`
  );
};

// Renders numeric price cells with tabular font alignment and bold emphasis on the latest price
const renderPrice = (value, isLatest = false) => {
  if (value === undefined || value === null || value === '-') {
    return h('span', { class: 'text-muted' }, '-');
  }
  return h('span', {
    style: {
      fontVariantNumeric: 'tabular-nums',
      fontWeight: isLatest ? '700' : '500',
      color: isLatest ? '#0b1a2d' : '#2c3e50',
    }
  }, typeof value === 'number' ? value.toLocaleString() : value);
};

// Helper function to render a header with a tooltip
const renderHeaderWithTooltip = (title, tooltipText) => {
  return () => h(
    NTooltip,
    { trigger: 'hover', placement: 'top' },
    {
      trigger: () => h('span', { style: 'text-decoration: underline dotted; cursor: help;' }, title),
      default: () => tooltipText
    }
  );
};

const renderCustomHeader = (dateObj) => {
  if (!dateObj || dateObj.rawStr === '-') return '-';
  
  if (dateObj.sub) {
    return () => h('div', { style: 'text-align: center; line-height: 1.2; padding: 4px 0;' }, [
      h('div', { style: 'font-size: 11px; color: #888;' }, dateObj.year),
      h('div', { style: 'font-size: 13px; color: #888;' }, dateObj.main),
      h('div', { style: 'font-weight: bold; font-size: 11px;' }, dateObj.sub)
    ]);
  }

  return () => h('div', { style: 'text-align: center; line-height: 1.2; padding: 4px 0;' }, [
    h('div', { style: 'font-size: 13px; color: #888;' }, dateObj.year),
    h('div', { style: 'font-weight: bold; font-size: 11px;' }, dateObj.main)
  ]);
};

const weeklyColumns = computed(() => {
  const dates = getHistoryDates('weekly');
  const baseColumns = [
    { title: 'Commodity', key: 'commodity', align: 'left', fixed: 'left', width: 90, ellipsis: { tooltip: true } },
    { title: 'Product', key: 'product', align: 'left', fixed: 'left', width: 180, ellipsis: { tooltip: true } },
    { title: 'Units', key: 'units', align: 'left', width: 75 },
    { title: 'Trend', key: 'trend', render: renderTrend, align: 'center', width: 75 },
  ];

  const total = dates.length;
  const dateColumns = dates.map((dateObj, index) => {
    const isLatest = index === total - 1;
    return {
      title: renderCustomHeader(dateObj),
      key: dateObj.rawStr,
      align: 'center',
      width: 85,
      render: (row) => renderPrice(row[dateObj.rawStr], isLatest)
    };
  });

  const endColumns = [
    { title: 'Date', key: 'date', align: 'center', width: 70 },
    { title: renderHeaderWithTooltip('W-o-W', 'Week-on-Week: (Current Week / Previous Week) - 1'), key: 'wow', align: 'center', width: 80, render: (row) => renderChange(row.wow, 'wow') },
  ];

  return [...baseColumns, ...dateColumns, ...endColumns];
});

const monthlyColumns = computed(() => {
  const dates = getHistoryDates('monthly');
  const baseColumns = [
    { title: 'Commodity', key: 'commodity', align: 'left', fixed: 'left', width: 90, ellipsis: { tooltip: true } },
    { title: 'Product', key: 'product', align: 'left', fixed: 'left', width: 180, ellipsis: { tooltip: true } },
    { title: 'Units', key: 'units', align: 'left', width: 75 },
    { title: 'Trend', key: 'trend', render: renderTrend, align: 'center', width: 75 },
  ];

  const total = dates.length;
  const dateColumns = dates.map((dateObj, index) => {
    const isLatest = index === total - 1;
    return {
      title: renderCustomHeader(dateObj),
      key: dateObj.rawStr,
      align: 'center',
      width: 85,
      render: (row) => renderPrice(row[dateObj.rawStr], isLatest)
    };
  });

  const endColumns = [
    { title: 'Date', key: 'date', align: 'center', width: 70 },
    { title: renderHeaderWithTooltip('M-o-M', 'Month-on-Month: (Current Month / Previous Month) - 1'), key: 'mom', align: 'center', width: 80, render: (row) => renderChange(row.mom, 'mom') },
  ];

  return [...baseColumns, ...dateColumns, ...endColumns];
});

// Dynamic scroll width so the table doesn't have squished columns or unnecessary blank space
const weeklyTableScrollX = computed(() => {
  const baseWidth = 570; // 90 (Commodity) + 180 (Product) + 75 (Units) + 75 (Trend) + 70 (Date) + 80 (WoW)
  const dates = getHistoryDates('weekly');
  return baseWidth + (dates.length * 85);
});

const monthlyTableScrollX = computed(() => {
  const baseWidth = 570;
  const dates = getHistoryDates('monthly');
  return baseWidth + (dates.length * 85);
});

// Computed columns for the Data Table view. 
// It dynamically generates columns for available dates (from oldest with data to newest completed)
const dataTableColumns = computed(() => {
  const freq = dataTableFrequency.value;
  // Get all historical periods with data up to latest completed (hides empty earlier weeks)
  const dates = getHistoryDates(freq);
  
  const baseColumns = [
    { title: 'Commodity', key: 'commodity', align: 'left', fixed: 'left', width: 90, ellipsis: { tooltip: true } },
    { title: 'Product', key: 'product', align: 'left', fixed: 'left', width: 180, ellipsis: { tooltip: true } },
    { title: 'Units', key: 'units', align: 'left', width: 75 },
    { title: 'Trend', key: 'trend', render: renderTrend, align: 'center', width: 75 }
  ];

  const total = dates.length;
  const periodColumns = dates.map((dateObj, index) => {
    const isLatest = index === total - 1;
    return {
      title: renderCustomHeader(dateObj),
      key: dateObj.rawStr,
      align: 'center',
      width: 80,
      render: (row) => renderPrice(row[dateObj.rawStr], isLatest)
    };
  });

  return [...baseColumns, ...periodColumns];
});

// Dynamic scroll width so the table doesn't have unnecessary blank horizontal scroll space
const dataTableScrollX = computed(() => {
  const baseWidth = 420; // Commodity (90) + Product (180) + Units (75) + Trend (75)
  const dates = getHistoryDates(dataTableFrequency.value);
  return baseWidth + (dates.length * 80);
});

// Export current Data Table records to Excel based on active filters
const handleExportDataTable = () => {
  try {
    const rows = dataTableRecords.value || [];
    if (rows.length === 0) {
      message.warning("No data available to export with the current filters.");
      return;
    }

    const freq = dataTableFrequency.value;
    const isWeekly = freq.toLowerCase() === 'weekly';
    const dates = getHistoryDates(freq);

    // Build Excel header row
    const headerRow = [
      "Commodity",
      "Product",
      "Product Code",
      "Units",
      ...dates.map(d => isWeekly ? `${d.main} (${d.sub}/${d.year})` : `${d.main} ${d.year}`)
    ];

    // Build Excel data rows based on currently applied filters
    const excelRows = rows.map(row => {
      const baseValues = [
        row.commodity || '',
        row.product || '',
        row.productCode || '',
        row.units || ''
      ];

      const periodValues = dates.map(d => {
        const val = row[d.rawStr];
        if (val === undefined || val === null || val === '-' || val === '') {
          return '-';
        }
        const num = Number(val);
        return isNaN(num) ? val : num;
      });

      return [...baseValues, ...periodValues];
    });

    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...excelRows]);

    // Set readable column widths
    const colWidths = [
      { wch: 14 }, // Commodity
      { wch: 35 }, // Product
      { wch: 16 }, // Product Code
      { wch: 14 }, // Units
      ...dates.map(() => ({ wch: 16 })) // Period date columns
    ];
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${freq} Pricing`);

    // File name reflects current filters
    const activeTabLabel = formatSupplyChain(activeSupplyChain.value) || 'All';
    const dateStamp = dayjs().format('YYYYMMDD');
    const fileName = `Pricing_Services_${freq}_${activeTabLabel.replace(/[^a-zA-Z0-9]/g, '_')}_${dateStamp}.xlsx`;

    XLSX.writeFile(workbook, fileName);

    message.success(`Exported ${rows.length} records to Excel.`);

    // Log the user action
    logPricingAction(
      "ExportPricingDataTable",
      `User ${localStorage.getItem("email") || userEmail} exported Pricing Services Data Table (${freq}) to "${fileName}" with filters: Supply Chain="${activeSupplyChain.value}", Commodity="${commodityFilter.value || 'All'}", Region="${regionFilter.value || 'All'}".`
    );
  } catch (err) {
    console.error("Export to Excel failed:", err);
    message.error("Failed to export data to Excel.");
  }
};

// Pagination configurations for each table
const weeklyPagination = ref({
  page: 1,
  pageSize: 10,
  showSizePicker: true,
  pageSizes: [10, 20, 50, 100],
  prefix: (info) => `Total: ${info.itemCount} items`,
  onChange: (page) => {
    weeklyPagination.value.page = page;
  },
  onUpdatePageSize: (pageSize) => {
    weeklyPagination.value.pageSize = pageSize;
    weeklyPagination.value.page = 1;
  }
});

const monthlyPagination = ref({
  page: 1,
  pageSize: 10,
  showSizePicker: true,
  pageSizes: [10, 20, 50, 100],
  prefix: (info) => `Total: ${info.itemCount} items`,
  onChange: (page) => {
    monthlyPagination.value.page = page;
  },
  onUpdatePageSize: (pageSize) => {
    monthlyPagination.value.pageSize = pageSize;
    monthlyPagination.value.page = 1;
  }
});

const dataTablePagination = ref({
  page: 1,
  pageSize: 15,
  showSizePicker: true,
  pageSizes: [10, 15, 25, 50, 100],
  prefix: (info) => `Total: ${info.itemCount} items`,
  onChange: (page) => {
    dataTablePagination.value.page = page;
  },
  onUpdatePageSize: (pageSize) => {
    dataTablePagination.value.pageSize = pageSize;
    dataTablePagination.value.page = 1;
  }
});

// Log user action using the shared logUserAction composable
const logPricingAction = (action, description, meta = {}) => {
  logUserAction(action, description, { endpoint: "/pricing-services", ...meta });
};

// Automatically download a historical file clicked in the dropdown
const handleHistoricalSelect = async (key) => {
  if (key === 'monthly-header' || key === 'weekly-header' || String(key).startsWith('header-')) return;
  const option = [...monthlyHistoricalOptions.value, ...weeklyHistoricalOptions.value].find(opt => opt.key === key);
  const file = option?.file || allSubscribedReports.value.find(f => String(f.fileUuid || f.id) === key);
  if (!file) {
    message.error("Selected report file not found.");
    return;
  }
  const isWeekly = isWeeklyFile(file);
  const reportType = isWeekly ? 'Weekly' : 'Monthly';
  try {
    message.info(`Preparing ${file.fileName || 'report'} for download...`);
    await downloadFileComposable(file.fileUuid || file.id, null, file.folderName);
    logPricingAction(
      "DownloadPricingReport",
      `User ${localStorage.getItem("email") || userEmail} downloaded Historical ${reportType} Report: "${file.fileName || file.displayName || 'report'}" (Supply Chain: "${formatSupplyChain(activeSupplyChain.value) || 'Commodity'}").`
    );
  } catch (e) {
    console.error("Historical download failed:", e);
    message.error("Failed to download historical report.");
  }
};

const handleReportClick = async (actionName) => {
  console.log(`Report action triggered: ${actionName}`);
  const isWeekly = actionName.toLowerCase().includes('weekly');
  const reportType = isWeekly ? 'Weekly' : 'Monthly';
  const files = isWeekly ? weeklyHistoricalFiles.value : monthlyHistoricalFiles.value;

  if (files.length > 0) {
    const latestFile = files[0];
    try {
      message.info(`Preparing ${latestFile.fileName || 'report'} for download...`);
      await downloadFileComposable(latestFile.fileUuid || latestFile.id, null, latestFile.folderName);
      logPricingAction(
        "DownloadPricingReport",
        `User ${localStorage.getItem("email") || userEmail} downloaded Latest ${reportType} Report: "${latestFile.fileName || latestFile.displayName || 'report'}" (Supply Chain: "${formatSupplyChain(activeSupplyChain.value) || 'Commodity'}").`
      );
    } catch (e) {
      console.error("Download failed:", e);
      message.error("Failed to download the file.");
    }
  } else {
    message.warning(`No ${isWeekly ? 'Weekly' : 'Monthly'} reports available for your subscription.`);
  }
};

// Track View changes: Supply Chain tabs
watch(activeSupplyChain, (newChain, oldChain) => {
  weeklyPagination.value.page = 1;
  monthlyPagination.value.page = 1;
  dataTablePagination.value.page = 1;
  if (!newChain || newChain === oldChain) return;
  logPricingAction(
    "ViewPricingServices",
    `User ${localStorage.getItem("email") || userEmail} viewed Pricing Services for supply chain: "${formatSupplyChain(newChain)}" (${activeView.value} view).`
  );
});

// Track View changes: View Mode tabs (Dashboard vs Data Table)
watch(activeView, (newView, oldView) => {
  weeklyPagination.value.page = 1;
  monthlyPagination.value.page = 1;
  dataTablePagination.value.page = 1;
  if (newView === oldView) return;
  logPricingAction(
    "ViewPricingServices",
    `User ${localStorage.getItem("email") || userEmail} switched Pricing Services view to: "${newView}" (Supply Chain: "${formatSupplyChain(activeSupplyChain.value) || 'Commodity'}").`
  );
});

// Track Filter / Search changes (Commodity, Region, Frequency) with debounce
let filterLogTimer = null;
watch([commodityFilter, regionFilter, dataTableFrequency], ([comm, reg, freq], [oldComm, oldReg, oldFreq]) => {
  weeklyPagination.value.page = 1;
  monthlyPagination.value.page = 1;
  dataTablePagination.value.page = 1;
  if (comm === oldComm && reg === oldReg && freq === oldFreq) return;

  clearTimeout(filterLogTimer);
  filterLogTimer = setTimeout(() => {
    const filters = [];
    if (comm) filters.push(`Commodity="${comm}"`);
    if (reg) filters.push(`Region="${reg}"`);
    if (activeView.value === 'DataTable' && freq) filters.push(`Frequency="${freq}"`);

    const filterDetails = filters.length > 0 ? filters.join(', ') : 'cleared filters';
    logPricingAction(
      "FilterPricingServices",
      `User ${localStorage.getItem("email") || userEmail} filtered Pricing Services: ${filterDetails} (Supply Chain: "${formatSupplyChain(activeSupplyChain.value) || 'Commodity'}").`
    );
  }, 500);
});

onMounted(async () => {
  inProgress.value = true;
  window.$loading.start();
  if (!USER_FILES.value?.files) await getUserFiles();
  await fetchPricingServices();
  if (USER_FILES.value?.files) window.$loading.finish();

  // Log initial page view
  logPricingAction(
    "ViewPricingServices",
    `User ${localStorage.getItem("email") || userEmail} viewed Pricing Services for supply chain: "${formatSupplyChain(activeSupplyChain.value) || 'Commodity'}" (${activeView.value} view).`
  );
});
</script>

<style scoped>
.dash-filterbar {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #f2f2f2;
  border-radius: 4px;
}

.dash-filterbar__select,
.dash-filterbar__product {
  width: 200px;
}

.dash-actions {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 8px 0px;
  background: transparent;
  flex-wrap: wrap;
  row-gap: 0.25rem;
}

.dash-action-btn {
  flex-shrink: 0;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  font-size: 13px;
  color: #000;
  cursor: pointer;
  padding: 4px 10px 4px 4px;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
}

.dash-action-btn--active {
  color: #2184ff;
  font-weight: 600;
  text-decoration: underline;
}

.dash-action-btn:hover:not(.dash-action-btn--active):not(:disabled) {
  color: #2184ff;
  font-weight: 600;
  text-decoration: underline;
}

.dash-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dash-action-btn__icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: block;
  border-radius: 50%;
}

.table-scroll.modern-scrollbar {
  overflow-y: visible;
  height: auto;
}

:deep(.n-pagination) {
  margin-top: 14px;
  justify-content: flex-end;
  align-items: center;
  font-size: 12px;
}

:deep(.n-pagination .n-pagination-prefix) {
  margin-right: auto;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.table-title{
  font-size: 14px;
  font-weight: bold;
  color: black;
}

.bg-soft-yellow {
  background-color: #fff3cd;
}

.bg-soft-green {
  background-color: #d1e7dd;
}

:deep(.n-data-table-th) {
  /*font-weight: bold !important; */
  color: black !important;
  background-color: #f8f9fa !important;
  text-transform: capitalize;
  white-space: nowrap;
}

:deep(.n-data-table-td) {
  padding: 8px 6px !important;
  font-size: 13px !important;
  vertical-align: middle;
}

:deep(.n-data-table-th) {
  padding: 8px 6px !important;
  font-size: 12px !important;
}

/* Responsive Reports Sidebar */
.pricing-reports-sidebar {
  width: 190px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

@media (max-width: 1200px) {
  .pricing-main-layout {
    flex-direction: column !important;
  }
  .pricing-reports-sidebar {
    width: 100% !important;
    flex-direction: row !important;
    justify-content: center !important;
    flex-wrap: wrap !important;
    gap: 24px !important;
    margin-top: 20px;
  }
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 250px;
  gap: 1.5rem;
}

.custom-loader {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.loader-circle {
  width: 12px;
  height: 12px;
  background-color: #0b1a2d;
  border-radius: 50%;
  animation: loader-bounce 1.4s ease-in-out infinite both;
}
.loader-circle:nth-child(1) { animation-delay: -0.32s; }
.loader-circle:nth-child(2) { animation-delay: -0.16s; }
.loader-circle:nth-child(4) { animation-delay: 0.16s; }

@keyframes loader-bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
  40%           { transform: scale(1); opacity: 1;   }
}

.loading-text {
  color: #0b1a2d;
  font-size: 1rem;
  font-weight: 500;
}

/* Report Cards & Split Buttons */
.report-card {
  width: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.report-card .report-split-group,
.report-card .report-thumb {
  width: 100% !important;
}

.report-thumb {
  border-radius: 4px;
  border: 1px solid #ccc;
  object-fit: cover;
}

.report-split-group {
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 36px;
  box-shadow: 0 2px 6px rgba(33, 60, 229, 0.18);
  border-radius: 4px;
  transition: all 0.2s ease;
}

.report-split-group:hover {
  box-shadow: 0 4px 10px rgba(33, 60, 229, 0.28);
}

.report-main-btn {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.report-main-btn:hover:not(:disabled) {
  opacity: 0.93;
}

.report-main-btn:disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

.report-dropdown-btn {
  width: 32px;
  min-width: 32px;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.report-dropdown-btn:hover:not(:disabled) {
  background-color: #174eb3 !important;
}

.report-dropdown-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
</style>
